import type { FeedbackAnswerQuestion, QuestionData, QuestionExportAnswer, QuestionSerialized } from '@repo/types/questions/Question';
import type { IQuestion } from '@repo/types/questions/Question.interface';
import type { QuizAttemptPrivate, QuizSection } from '@repo/types/quiz';

import { QuestionFactory } from '#quiz/questions/class/QuestionFactory';

export class QuizDataResolver {
  static updateQuestionsMarkedStatus(serializedContent: QuestionSerialized[][], content: IQuestion[][], marked?: FeedbackAnswerQuestion[][]) {
    if (!marked) {
      return { serializedContent, content };
    }

    const updatedSerializedContent = this.cloneSerializedContent(serializedContent);
    const updatedContent = this.cloneQuestionContent(content);

    this.applyMarkedStatusToQuestions(updatedSerializedContent, updatedContent, marked);

    return {
      serializedContent: updatedSerializedContent,
      content: updatedContent,
    };
  }

  static buildQuestionsFromLocalData(quizSections: QuizSection[], serializedContent: QuestionSerialized[][], attemptData: QuizAttemptPrivate): IQuestion[][] {
    const factory = new QuestionFactory();
    const { body } = attemptData;

    return quizSections.map((section, sectionIndex) =>
      section.questions.map((questionData, questionIndex) => {
        const serializedQuestion = this.getSerializedQuestion(serializedContent, sectionIndex, questionIndex);
        const markedData = this.getMarkedData(body.marked, sectionIndex, questionIndex);

        return factory.createQuestion(questionData, serializedQuestion?.selectedAnswer, markedData);
      })
    );
  }

  static createQuestionWithData(questionData: QuestionData, answer?: QuestionExportAnswer, markedData?: FeedbackAnswerQuestion): IQuestion {
    const factory = new QuestionFactory();
    const questionInstance = factory.createQuestion(questionData);
    if (answer) {
      questionInstance.importAnswer(answer);
    }
    if (markedData) {
      questionInstance.setMarked(markedData);
    }

    return questionInstance;
  }

  static buildQuestionsFromApiData(quizSections: QuizSection[], attemptData: QuizAttemptPrivate) {
    const { body } = attemptData;
    const serializedResult: QuestionSerialized[][] = [];
    const classResult: IQuestion[][] = [];

    for (const [sectionIndex, section] of quizSections.entries()) {
      const { serializedSection, instanceSection } = this.processSectionQuestions(section, sectionIndex, body);

      serializedResult.push(serializedSection);
      classResult.push(instanceSection);
    }

    return { serializedResult, classResult };
  }

  private static cloneSerializedContent(content: QuestionSerialized[][]): QuestionSerialized[][] {
    return [...content];
  }

  private static cloneQuestionContent(content: IQuestion[][]): IQuestion[][] {
    return [...content];
  }

  private static applyMarkedStatusToQuestions(serializedContent: QuestionSerialized[][], content: IQuestion[][], markedQuestions: FeedbackAnswerQuestion[][]): void {
    for (const [sectionIndex, section] of markedQuestions.entries()) {
      for (const [questionIndex, markedData] of section.entries()) {
        this.updateQuestionMarkedStatus(serializedContent, content, sectionIndex, questionIndex, markedData);
      }
    }
  }

  private static updateQuestionMarkedStatus(serializedContent: QuestionSerialized[][], content: IQuestion[][], sectionIndex: number, questionIndex: number, markedData: FeedbackAnswerQuestion): void {
    const serializedQuestion: QuestionSerialized | undefined = serializedContent[sectionIndex]?.[questionIndex];
    const question = content[sectionIndex]?.[questionIndex];
    if (question) {
      question.setMarked(markedData);
    }

    if (serializedQuestion) {
      serializedQuestion.marked = markedData;
    }
    if (serializedQuestion && question) {
      serializedQuestion.marked = markedData;
    }
  }

  private static getSerializedQuestion(serializedContent: QuestionSerialized[][], sectionIndex: number, questionIndex: number): QuestionSerialized | undefined {
    return serializedContent[sectionIndex]?.[questionIndex];
  }

  private static getMarkedData(markedQuestions: FeedbackAnswerQuestion[][] | undefined, sectionIndex: number, questionIndex: number): FeedbackAnswerQuestion | undefined {
    return markedQuestions?.[sectionIndex]?.[questionIndex];
  }

  private static processSectionQuestions(section: QuizSection, sectionIndex: number, attemptBody: QuizAttemptPrivate['body']) {
    const serializedSection: QuestionSerialized[] = [];
    const instanceSection: IQuestion[] = [];

    const sectionAnswers = attemptBody.answers?.[sectionIndex] || [];
    const sectionMarked = attemptBody.marked?.[sectionIndex] || [];

    for (const [questionIndex, questionData] of section.questions.entries()) {
      const answer = sectionAnswers[questionIndex];
      const markedData = sectionMarked[questionIndex];

      const questionInstance = this.createQuestionWithData(questionData, answer, markedData);
      const serializedData = questionInstance.toJSON();

      serializedSection.push(serializedData as QuestionSerialized);
      instanceSection.push(questionInstance);
    }

    return { serializedSection, instanceSection };
  }
}
